// Packing BOMs — what one unit of a finished item takes to fill and pack
// (bulk oil, drum/bucket, cap, seal, label, carton). Material requests can be
// filled from a BOM. Server: src/api-inventory/packing-boms.js.
import { useEffect, useState } from 'react';
import { Layers, Pencil, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/shared/components/ui/Button';
import { Modal } from '@/shared/components/ui/Modal';
import { Select } from '@/shared/components/ui/Select';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { materialErrorText } from '../hooks/useMaterials';
import { usePackingBoms, useSaveBom, useDeleteBom, type PackingBom } from '../hooks/useStockControl';
import { FieldLabel, ItemNameInput, Panel, Pill, fmtDateTime, fmtQty } from './materialShared';

const UNITS = ['NOS', 'LTR', 'KG', 'ML', 'GM', 'BOX', 'ROLL'];
const CATEGORY_LABEL: Record<string, string> = { raw_material: 'Raw Material', packaging_material: 'Packaging Material', consumables: 'Consumables' };

export function PackingBomsTab() {
  const { data, isLoading } = usePackingBoms();
  const del = useDeleteBom();
  const [editing, setEditing] = useState<PackingBom | 'new' | null>(null);
  const boms = data?.boms || [];
  return (
    <Panel
      title="Packing BOMs"
      subtitle="What it takes to fill and pack one unit of each finished item. Raise a material request from a BOM and the quantities fill themselves."
      actions={data?.canEdit ? <Button onClick={() => setEditing('new')}><Plus className="h-4 w-4" /> New BOM</Button> : undefined}
    >
      {isLoading ? <div className="p-5"><Skeleton className="h-40" /></div> : (
        <div className="divide-y divide-border">
          {boms.map((b) => (
            <div key={b._id} className="flex flex-wrap items-start justify-between gap-3 px-5 py-3">
              <div className="min-w-0">
                <div className="font-medium text-on-surface">{b.finishedName}</div>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {b.components.map((c, i) => (
                    <span key={i} className="rounded-md bg-surface-container-low px-2 py-0.5 text-data-xs text-on-surface-variant">
                      {fmtQty(c.qtyPerUnit)} {c.unit} {c.itemName}{c.source === 'tank' ? ' (tank)' : ''}
                    </span>
                  ))}
                </div>
                <div className="mt-1 text-data-xs text-on-surface-subtle">Per 1 unit · updated by {b.updatedByName || '—'} · {fmtDateTime(b.updatedAt)}</div>
              </div>
              {data?.canEdit && (
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => setEditing(b)}><Pencil className="h-3.5 w-3.5" /> Edit</Button>
                  <Button size="sm" variant="ghost" aria-label={`Remove BOM for ${b.finishedName}`} onClick={async () => {
                    if (!window.confirm(`Remove the BOM for ${b.finishedName}?`)) return;
                    try { await del.mutateAsync(b._id); toast.success('BOM removed'); } catch (err) { toast.error(materialErrorText(err, 'Could not remove.')); }
                  }}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              )}
            </div>
          ))}
          {!boms.length && (
            <div className="flex flex-col items-center gap-2 px-5 py-12 text-center">
              <Layers className="h-8 w-8 text-on-surface-subtle" aria-hidden />
              <p className="text-body-md font-medium text-on-surface">No BOMs yet</p>
              <p className="max-w-md text-body-sm text-on-surface-variant">Start with the items filled most often, e.g. a 210 L drum: 210 LTR base oil from the tank, 1 drum, 1 cap, 1 seal, 1 label.</p>
            </div>
          )}
        </div>
      )}
      {editing && <BomModal bom={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </Panel>
  );
}

type Row = { key: number; productId: string | null; itemName: string; qtyPerUnit: string; unit: string; category: string; source: 'warehouse' | 'tank' };
let k = 1;
const blank = (p?: Partial<Row>): Row => ({ key: k++, productId: null, itemName: '', qtyPerUnit: '', unit: 'NOS', category: 'packaging_material', source: 'warehouse', ...p });

function BomModal({ bom, onClose }: { bom: PackingBom | null; onClose: () => void }) {
  const save = useSaveBom();
  const [finished, setFinished] = useState<{ itemName: string; productId: string | null }>({ itemName: bom?.finishedName || '', productId: bom?.finishedProductId || null });
  const [rows, setRows] = useState<Row[]>([]);
  const [notes, setNotes] = useState(bom?.notes || '');
  useEffect(() => {
    setRows(bom ? bom.components.map((c) => blank({ productId: c.productId, itemName: c.itemName, qtyPerUnit: String(c.qtyPerUnit), unit: c.unit, category: c.category, source: c.source })) : [
      blank({ category: 'raw_material', unit: 'LTR', source: 'tank' }), blank(), blank(), blank({ category: 'consumables' }),
    ]);
  }, [bom]);
  const patch = (key: number, p: Partial<Row>) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...p } : r)));
  const submit = async () => {
    try {
      await save.mutateAsync({
        id: bom?._id, finishedProductId: finished.productId || undefined, finishedName: finished.itemName.trim(), notes,
        components: rows.map((r) => ({ productId: r.productId, itemName: r.itemName, qtyPerUnit: Number(r.qtyPerUnit), unit: r.unit, category: r.category as never, source: r.source })),
      });
      toast.success('BOM saved');
      onClose();
    } catch (err) { toast.error(materialErrorText(err, 'Could not save the BOM.')); }
  };
  return (
    <Modal open onClose={onClose} size="2xl" title={bom ? `Edit BOM — ${bom.finishedName}` : 'New packing BOM'} footer={(
      <><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={submit} loading={save.isPending} disabled={!finished.productId && !finished.itemName.trim()}>Save BOM</Button></>
    )}>
      <div className="space-y-4">
        <div>
          <FieldLabel required>Finished item (1 unit)</FieldLabel>
          {bom ? <div className="rounded-lg border border-border bg-surface-container-low px-3 py-2 text-body-sm font-medium text-on-surface">{bom.finishedName}</div>
            : <ItemNameInput value={finished.itemName} onChange={(n) => setFinished({ itemName: n.itemName, productId: n.productId })} placeholder="e.g. RBM HYDRAULIC OIL AW 68 210 LTR" />}
        </div>
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full min-w-[820px] text-body-sm">
            <thead className="bg-surface-container-low/60 text-left text-label-caps uppercase text-on-surface-variant">
              <tr><th className="px-3 py-2">Component</th><th className="w-28 px-2 py-2">Per unit</th><th className="w-24 px-2 py-2">Unit</th><th className="w-44 px-2 py-2">Type</th><th className="w-40 px-2 py-2">From</th><th className="w-10" /></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => (
                <tr key={r.key} className="align-top">
                  <td className="px-3 py-2"><ItemNameInput value={r.itemName} onChange={(n) => patch(r.key, { itemName: n.itemName, productId: n.productId })} /></td>
                  <td className="px-2 py-2"><input type="number" min="0" step="any" value={r.qtyPerUnit} onChange={(e) => patch(r.key, { qtyPerUnit: e.target.value })} className="h-9 w-full rounded-lg border border-input bg-surface px-2 text-right font-mono outline-none focus:border-primary" /></td>
                  <td className="px-2 py-2"><Select value={r.unit} onChange={(e) => patch(r.key, { unit: e.target.value })} options={UNITS.map((u) => ({ value: u, label: u }))} /></td>
                  <td className="px-2 py-2"><Select value={r.category} onChange={(e) => patch(r.key, { category: e.target.value })} options={Object.entries(CATEGORY_LABEL).map(([value, label]) => ({ value, label }))} /></td>
                  <td className="px-2 py-2"><Select value={r.source} onChange={(e) => patch(r.key, { source: e.target.value as Row['source'] })} options={[{ value: 'warehouse', label: 'Store' }, { value: 'tank', label: 'Tank (bulk)' }]} /></td>
                  <td className="px-1 py-2">{rows.length > 1 && <button type="button" aria-label="Remove component" onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))} className="rounded p-1.5 text-on-surface-subtle hover:bg-danger/10 hover:text-danger-text"><Trash2 className="h-4 w-4" /></button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="border-t border-border px-3 py-2"><Button variant="ghost" size="sm" onClick={() => setRows((rs) => [...rs, blank()])}><Plus className="h-4 w-4" /> Add component</Button></div>
        </div>
        <label className="block"><FieldLabel>Notes</FieldLabel>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full rounded-lg border border-input bg-surface px-3 py-2 text-body-sm outline-none focus:border-primary" />
        </label>
        <p className="text-body-sm text-on-surface-variant"><Pill tone="info">Tip</Pill> Pick components from the Item Master so issuing them updates stock. Empty rows are ignored.</p>
      </div>
    </Modal>
  );
}

export default PackingBomsTab;
